import { useCallback, useEffect, useState } from "react";
import { ArrowRight, FolderOpen, LogIn, RefreshCw, Search } from "lucide-react";
import { fetchNevisignCases, importAdliranCases } from "@nevisign/reader-core";
import { useAuth } from "../contexts/auth-context";
import { requestAuthForAction } from "../lib/auth-required";
import { ensureFreshAccessToken } from "../lib/platform-auth";

type CasesResult = Awaited<ReturnType<typeof fetchNevisignCases>>;

type Props = {
  apiBase: string;
  onBack: () => void;
  onOpenCase: (caseId: string) => void;
};

function faNum(value: number | string): string {
  return String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]!);
}

export function CasesListPage({ apiBase, onBack, onOpenCase }: Props) {
  const { isAuthenticated, openLogin } = useAuth();
  const [result, setResult] = useState<CasesResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setResult(null);
      return;
    }
    setLoading(true);
    await ensureFreshAccessToken();
    const res = await fetchNevisignCases(apiBase, {
      search: search.trim() || undefined,
      pageSize: 50,
    });
    setResult(res);
    setLoading(false);
    if (res.error?.includes("وارد")) {
      requestAuthForAction(() => void load());
    }
  }, [apiBase, isAuthenticated, search]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="cases-list-page consumer-shell">
      <header className="list-page-hero">
        <button type="button" className="text-back" onClick={onBack}>
          <ArrowRight size={16} />
          بازگشت
        </button>
        <div className="list-page-hero-copy">
          <span className="consumer-kicker">پرونده‌های من</span>
          <h1>پرونده‌ها</h1>
          <p>
            پرونده‌ها از کارتابل اصلی شما خوانده می‌شوند؛ برای بررسی وضعیت،
            شعبه، طرفین و رویدادها وارد جزئیات شوید.
          </p>
        </div>
      </header>

      {!isAuthenticated ? (
        <div className="dashboard-auth-prompt">
          <p>برای مشاهده پرونده‌ها وارد حساب نویساین شوید.</p>
          <button type="button" className="icon-btn primary" onClick={() => openLogin()}>
            <LogIn size={16} />
            ورود
          </button>
        </div>
      ) : (
        <>
          <form
            className="list-search-card"
            onSubmit={(event) => {
              event.preventDefault();
              void load();
            }}
          >
            <Search size={16} aria-hidden />
            <input
              type="search"
              dir="rtl"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="جستجو در شماره، شعبه یا موضوع پرونده…"
              aria-label="جستجوی پرونده‌ها"
            />
            <button type="submit" className="icon-btn compact" disabled={loading}>
              جستجو
            </button>
          </form>

          <div className="list-page-toolbar">
            <span>
              {result ? `${faNum(result.items.length)} پرونده` : "در حال آماده‌سازی"}
            </span>
            <button type="button" className="icon-btn compact" disabled={loading} onClick={() => void load()}>
              <RefreshCw size={14} />
              بروزرسانی
            </button>
            <button
              type="button"
              className="icon-btn compact"
              disabled={loading || importing}
              onClick={() => {
                void (async () => {
                  setImporting(true);
                  await ensureFreshAccessToken();
                  const sync = await importAdliranCases(apiBase);
                  setImporting(false);
                  if (!sync.ok) {
                    setResult((prev) =>
                      prev ? { ...prev, error: sync.error } : { items: [], total: 0, source: "error", error: sync.error },
                    );
                    return;
                  }
                  await load();
                })();
              }}
            >
              <RefreshCw size={14} />
              {importing ? "در حال ورود از عدل ایران…" : "ورود از عدل ایران"}
            </button>
          </div>

          {result?.error ? (
            <div className="status-banner" role="alert">
              {result.error}
            </div>
          ) : null}

          {loading && !result ? (
            <div className="dashboard-skeleton-list">
              <div className="dashboard-skeleton-row" />
              <div className="dashboard-skeleton-row" />
              <div className="dashboard-skeleton-row" />
            </div>
          ) : null}

          {!loading && result?.items.length === 0 ? (
            <div className="dashboard-empty">
              <p>پرونده‌ای برای نمایش پیدا نشد.</p>
            </div>
          ) : null}

          <ul className="cases-list">
            {(result?.items ?? []).map((item) => (
              <li key={item.id}>
                <button type="button" className="case-list-card" onClick={() => onOpenCase(item.id)}>
                  <span className="case-list-icon" aria-hidden>
                    <FolderOpen size={18} />
                  </span>
                  <span className="case-list-copy">
                    <strong>پرونده {faNum(item.caseNo)}</strong>
                    <small>
                      {item.currentStatusLabel}
                      {item.primaryBranchName ? ` · ${item.primaryBranchName}` : ""}
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
